<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useUISettings } from 'dashboard/composables/useUISettings';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import InboxesAPI from 'dashboard/api/inboxes';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments, inboxId } =
  useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

const { uiSettings, updateUISettings } = useUISettings();

const isSticker = computed(() => attachment.value?.extension === 'webp');

const isStickerSaved = computed(() => {
  const list = uiSettings.value?.saved_stickers || [];
  return list.includes(attachment.value?.dataUrl);
});

const convertImageToDataUrl = url =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/webp'));
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = url;
  });

const convertBlobToDataUrl = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const saveSticker = async () => {
  const url = attachment.value?.dataUrl;
  if (!url) return;
  try {
    let dataUrl = null;
    try {
      dataUrl = await convertImageToDataUrl(url);
    } catch (error) {
      // fallback: baixa via fetch e converte
      const blob = await (
        await fetch(url, { credentials: 'same-origin' })
      ).blob();
      dataUrl = await convertBlobToDataUrl(blob);
    }
    if (!dataUrl) return;

    await InboxesAPI.saveWuzapiSticker(inboxId.value, dataUrl);
    useAlert('Figurinha salva nas suas figurinhas!');
  } catch (error) {
    useAlert('Falha ao salvar a figurinha.');
  }
};

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else-if="isLoaded" class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        :class="{ 'cw-sticker-img': isSticker }"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
      />
      <button
        v-if="isSticker"
        type="button"
        v-tooltip.top-end="isStickerSaved ? 'Figurinha salva' : 'Salvar figurinha'"
        class="cw-sticker-save flex items-center justify-center size-7 rounded-full"
        :class="isStickerSaved ? 'bg-n-teal-9 text-white' : 'bg-n-slate-12/60 text-white hover:bg-n-slate-12/80'"
        @click.stop="saveSticker"
      >
        <Icon :icon="isStickerSaved ? 'i-lucide-check' : 'i-lucide-heart'" class="size-3.5" />
      </button>
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>

<style>
.cw-sticker-img {
  max-width: 120px;
  max-height: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.cw-sticker-save {
  position: absolute;
  top: 6px;
  right: 6px;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 3;
}

.group:hover .cw-sticker-save {
  opacity: 1;
}
</style>
